import React from 'react'
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Checkbox,
    useTheme,
    TableSortLabel,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import clsx from 'clsx'
import { Loader, Tooltip } from 'packages/eid-ui'
import { Trans, useTranslation } from 'react-i18next'
import { Icon } from 'packages/eid-icons'

const StyledTable = styled(Table)(({ theme }) => ({
    backgroundColor: theme?.palette?.background?.paper,
    '& tr:hover': {
        backgroundColor: '#f7f8fa',
    },
}))

const StyledTableRow = styled(TableRow)(() => ({
    border: ' solid 1px #efeff1',
}))

const StyledTableCell = styled(TableCell)(() => ({
    maxWidth: '220px',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    overflow: 'hidden',
    padding: '14px 10px',
    fontSize: '13px',
    borderBottom: '0',
    '&:first-of-type': {
        paddingLeft: '31px',
    },
}))

const StyledTableHeaderCell = styled(TableCell)(() => ({
    overflowWrap: 'break-word',
    maxWidth: '220px',
    wordBreak: 'normal',
    overflow: 'hidden',
    lineHeight: '15px',
    fontSize: '12px',
    padding: '14px 10px',
    backgroundImage:
        'linear-gradient(0deg, rgb(0, 0, 0, 0.02) 12.5%, rgb(255, 255, 255) 12.5%, rgb(255, 255, 255) 50%, rgb(0, 0, 0, 0.02) 50%, rgb(0, 0, 0, 0.02) 62.5%, rgb(255, 255, 255) 62.5%, rgb(255, 255, 255) 100%)',
    backgroundSize: '8.00px 8.00px',
    '& svg': {
        margin: '0px !important',
    },
}))

const GreyText = styled('span')(() => ({
    color: '#7d7c7c !important',
}))

const TableGrid = (props) => {
    const {
        data,
        attributes,
        loading,
        rowSelection,
        fallbackMessage,
        tableClasses,
        onSort,
        sortBy,
        sortOrder,
    } = props

    const theme = useTheme()
    const { t } = useTranslation()

    const [selected, setSelected] = React.useState([])

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelecteds = data.map((n) => n.id)
            setSelected(newSelecteds)
            return
        }
        setSelected([])
    }

    const handleClick = (item) => {
        const selectedIndex = selected.indexOf(item.id)
        let newSelected = []

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, item.id)
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1))
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1))
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            )
        }

        setSelected(newSelected)
    }

    const handleSort = (data) => {
        const sortingProperty =
            data.columnName && data.columnName.length > 0
                ? data.columnName
                : data.name

        onSort(
            data,
            sortBy !== sortingProperty
                ? 'desc'
                : !sortOrder || sortOrder === '' || sortOrder === 'asc'
                ? 'desc'
                : 'asc',
        )
    }

    const isSelected = (id) => selected.indexOf(id) !== -1

    const getValue = (attribute, item) => {
        if (attribute.resolve) {
            return attribute.resolve(item)
        }
        return item[attribute.name] ?? '-'
    }

    const getTableGridRowLabel = (item) => {
        const header = attributes.find((a) => a.isHeader)
        if (header) {
            const v = getValue(header, item)
            const s = String(v ?? '').trim()
            if (s && s !== '-') return s
        }
        if (item?.name != null && String(item.name).trim())
            return String(item.name).trim()
        return item?.id != null ? `Row ${item.id}` : 'Table row'
    }

    const EXPLAIN_GRID_MAX = 260
    const buildTableGridCellExplain = (rowLabel, columnLabel, attr, item) => {
        const v = getValue(attr, item)
        const raw = String(v ?? '')
            .replace(/\s+/g, ' ')
            .trim()
        const detail =
            raw.length > 100 ? `${raw.slice(0, 97)}…` : raw || '(empty)'
        const hint = `Table · Row: ${rowLabel} · Column: ${columnLabel} · ${detail}`
        return hint.length > EXPLAIN_GRID_MAX
            ? `${hint.slice(0, EXPLAIN_GRID_MAX - 1)}…`
            : hint
    }

    return (
        <StyledTable>
            <TableHead variant="eidHead">
                <StyledTableRow>
                    {rowSelection && (
                        <TableCell
                            padding="checkbox"
                            data-ai-explain="Select or clear all rows on this page"
                            data-ai-explain-section="listing"
                        >
                            <Checkbox
                                icon={<Icon name="CheckBox" />}
                                checkedIcon={
                                    <Icon
                                        name="CheckedBox"
                                        color={theme?.palette?.primary?.main}
                                    />
                                }
                                checked={
                                    data.length > 0 &&
                                    selected.length === data.length
                                }
                                onChange={handleSelectAllClick}
                            />
                        </TableCell>
                    )}

                    {attributes.map((a, index) => {
                        const sortingProperty =
                            a.columnName && a.columnName.length > 0
                                ? a.columnName
                                : a.name
                        return (
                            <StyledTableHeaderCell
                                key={index}
                                component={StyledTableCell}
                                align={a.align}
                                data-ai-explain={`Column: ${a.label || a.name}`}
                                data-protectedsubcomponent={
                                    a?.requireAccess?.control || ''
                                }
                            >
                                <GreyText>
                                    {!a.sortable ? (
                                        a.label
                                    ) : (
                                        <span style={{ display: 'flex' }}>
                                            <Tooltip
                                                title={
                                                    sortBy ===
                                                        sortingProperty &&
                                                    sortOrder === 'desc'
                                                        ? t(
                                                              'Common_SortAscending',
                                                          )
                                                        : t(
                                                              'Common_SortDescending',
                                                          )
                                                }
                                            >
                                                <TableSortLabel
                                                    active={a.sortable}
                                                    direction={
                                                        sortBy ===
                                                        sortingProperty
                                                            ? sortOrder
                                                            : 'asc'
                                                    }
                                                    style={{
                                                        color:
                                                            sortBy ===
                                                                sortingProperty &&
                                                            'yellow !important',
                                                    }}
                                                    onClick={() =>
                                                        handleSort(a)
                                                    }
                                                >
                                                    {a.label}
                                                </TableSortLabel>
                                            </Tooltip>
                                        </span>
                                    )}
                                </GreyText>
                            </StyledTableHeaderCell>
                        )
                    })}
                </StyledTableRow>
            </TableHead>
            <TableBody>
                {loading && (
                    <TableRow>
                        <TableCell
                            colSpan={attributes.length}
                            style={{ textAlign: 'center' }}
                        >
                            <Loader />
                        </TableCell>
                    </TableRow>
                )}
                {data &&
                    (data.length === 0 ? (
                        <StyledTableRow data-ai-explain="Empty table: no rows">
                            <StyledTableCell
                                colSpan={attributes.length}
                                style={{ textAlign: 'center' }}
                            >
                                {fallbackMessage}
                            </StyledTableCell>
                        </StyledTableRow>
                    ) : (
                        <>
                            {data.map((item) => {
                                const rowLabel = getTableGridRowLabel(item)
                                return (
                                <StyledTableRow key={item.id}>
                                    {rowSelection && (
                                        <TableCell
                                            padding="checkbox"
                                            data-ai-explain="Select this row"
                                            data-ai-explain-section="listing"
                                        >
                                            <Checkbox
                                                icon={<Icon name="CheckBox" />}
                                                checkedIcon={
                                                    <Icon
                                                        name="CheckedBox"
                                                        color={
                                                            theme.palette
                                                                .primary.main
                                                        }
                                                    />
                                                }
                                                checked={isSelected(item.id)}
                                                onClick={() =>
                                                    handleClick(item)
                                                }
                                            />
                                        </TableCell>
                                    )}

                                    {attributes.map((a, index) => (
                                        <StyledTableCell
                                            key={`${item.id}${index}`}
                                            style={a.style}
                                            data-ai-explain={buildTableGridCellExplain(
                                                rowLabel,
                                                a.label || a.name,
                                                a,
                                                item,
                                            )}
                                            data-ai-explain-section="listing"
                                        >
                                            {getValue(a, item)}
                                        </StyledTableCell>
                                    ))}
                                </StyledTableRow>
                                )
                            })}
                        </>
                    ))}
            </TableBody>
        </StyledTable>
    )
}

TableGrid.defaultProps = {
    fallbackMessage: <Trans i18nKey="Common_NoDataFound" />,
}
export default TableGrid
